import * as React from 'react';
import { useEffect, useMemo, useRef } from 'react';

import { use$ } from '@legendapp/state/react';
import { CTA } from '@volkswagen-onehub/components-core';
import { Info } from '@volkswagen-onehub/icons-core';
import styled from 'styled-components';

import { useDisclaimerState } from '../../store/hooks/use-disclaimer-state';

import translate from '../../utils/translate';

import {
	deviceSettingStore,
	disclaimerStore,
	disclaimerStoreActions,
	dragOverlayStore,
	emissionStore,
	interiorStore,
	rootStore
} from '../../store';
import { testId } from '../../test-utils/constants';
import { Disclaimer } from '../efficiency-disclaimer/disclaimer';

export const REFERENCE_BADGE_ID = testId('Header_Title_Disclaimer_Button');

function BadgeCta() {
	const isEmissionDataVisible = use$(emissionStore.isEmissionDataVisible);
	const isMobileDevice = use$(deviceSettingStore.isMobileDevice);

	const [_, currentModelValue] = use$(rootStore.currentModel);
	const modelKey = currentModelValue.vehicleConfiguration?.modelKey || '';

	const referenceBadgeRef = useRef<HTMLButtonElement | null>(null);
	const closeDisclaimerRef = useRef<HTMLButtonElement | null>(null);

	const footerDisclaimer = useMemo(() => {
		const disclaimers = rootStore.translations.get()?.disclaimers;
		const override = modelKey ? disclaimers?.imageFooterOverrides?.[modelKey] : undefined;

		return override ?? disclaimers?.imageFooter ?? '';
	}, [modelKey]);

	const closeAriaLabel = translate('global.ariaLabel.hideType6Disclaimer');

	// Disclaimer needs to be here for reasons of DOM order (accessibility)
	const disclaimerId = 'header';
	useDisclaimerState({ id: disclaimerId, text: footerDisclaimer, isOpenOnLoad: isEmissionDataVisible });

	const isDragOverlayVisible = use$(dragOverlayStore.isDragOverlayVisible);
	const activeDisclaimer = use$(disclaimerStore.activeDisclaimer);
	const isDisclaimerVisible = activeDisclaimer?.id === disclaimerId;

	const wasDisclaimerVisibleBeforeRotation = useRef(isDisclaimerVisible);

	useEffect(() => {
		function handleOrientationChange() {
			if (wasDisclaimerVisibleBeforeRotation.current) {
				// Uses timeout to trigger on partially completed rotation
				setTimeout(() => {
					disclaimerStoreActions.openDisclaimer(disclaimerId);
				}, 100);
			}
		}

		window.addEventListener('orientationchange', handleOrientationChange);

		// No need to focus the disclaimer on mobile devices neither when taptointeract active
		if (isMobileDevice || isDragOverlayVisible)
			return () => {
				window.removeEventListener('orientationchange', handleOrientationChange);
			};

		if (!referenceBadgeRef.current) return;

		if (isDisclaimerVisible && closeDisclaimerRef.current) {
			// Hack, focus is lost for about ~500ms, we refocus to bring focus back to element
			setTimeout(() => {
				closeDisclaimerRef.current?.focus();
			}, 300);
		}

		interiorStore.isInteriorLoaded.onChange(() => setTimeout(() => referenceBadgeRef.current?.focus(), 300));

		if (!isDisclaimerVisible) {
			setTimeout(() => referenceBadgeRef.current?.focus(), 300);
		}

		return () => {
			window.removeEventListener('orientationchange', handleOrientationChange);
		};
	}, [isDisclaimerVisible, isDragOverlayVisible]);

	function handleDisclaimerToggle() {
		if (isDisclaimerVisible) {
			disclaimerStoreActions.closeDisclaimer();
			wasDisclaimerVisibleBeforeRotation.current = false;
		} else {
			disclaimerStoreActions.openDisclaimer(disclaimerId);
			wasDisclaimerVisibleBeforeRotation.current = true;
		}
	}

	return (
		<>
			<ReferenceBadgeWrapper>
				<CTA
					tag="button"
					emphasis="tertiary"
					ref={referenceBadgeRef}
					data-testid={REFERENCE_BADGE_ID}
					onClick={handleDisclaimerToggle}
					aria-label={
						isDisclaimerVisible
							? translate('global.ariaLabel.hideType6Disclaimer')
							: translate('global.ariaLabel.showType6Disclaimer')
					}
					size="small"
					aria-controls={testId('Disclaimer_Container')}
					icon={<Info />}
				/>
			</ReferenceBadgeWrapper>

			{isDisclaimerVisible && (
				<Disclaimer
					text={footerDisclaimer}
					closeDisclaimerRef={closeDisclaimerRef}
					closeAriaLabel={closeAriaLabel}
					closeDisclaimer={handleDisclaimerToggle}
				/>
			)}
		</>
	);
}

export default BadgeCta;

const ReferenceBadgeWrapper = styled.span`
	padding-right: ${({ theme }) => theme.size.static150};
	padding-left: ${({ theme }) => theme.size.static150};

	button {
		height: 24px;
		width: 24px;
		border: none;

		:hover *,
		:active * {
			color: ${({ theme }) => theme.colors.content.hover};
		}

		// Avoids unwanted inheritance
		color: ${({ theme }) => theme.colors.content.primary};

		${({ theme }) =>
			theme.variant === 'v26' &&
			`
				svg,
				svg [stroke] {
					color: #ffffff;
				}
			`}
	}
`;
